import { Vector3 } from "three";
import { Data } from "ws";
import { MultiString } from "../../../i18n/MultiString";
import { ExpressionNode } from "../../../text/lexer/expressions/ExpressionNode";
import { DataType } from "../../evaluators/DataType";
import { EvaluationResult } from "../../evaluators/EvaluationResult";
import { Evaluator } from "../../evaluators/Evaluator";
import { ParameterDataType } from "../../evaluators/ParameterDataType";
import { FunctionExpression } from "../../expressions/FunctionExpression";
import { RJExpressionNode } from "../../expressions/RJExpressionNode";
import { RJExpressionNodeType } from "../../expressions/RJExpressionNodeType";
import { SequenceExpression } from "../../expressions/SequenceExpression";
import { RJParserMessages } from "../RJParserMessages";
import { DataTypes } from "./Library/DataTypes";
import { LomaInfo } from "./LomaInfo";

export type LomaFunctionCallback = (e:Evaluator, fe:FunctionExpression, values:EvaluationResult<any>[] )=>EvaluationResult<any>;

export class LomaFunction extends LomaInfo
{
  functionName:string;
  callback:LomaFunctionCallback;
  parameters:ParameterDataType<any>[];
  returnType:DataType;
  
  callFunction( evaluator:Evaluator, functionExpression:FunctionExpression, values:EvaluationResult<any>[] ):EvaluationResult<any>
  {
    return this.callback( evaluator, functionExpression, values );
  }

  getInfoTitle(){ return this.functionName; }

  checkParameters( values:EvaluationResult<any>[] )
  {    
    for ( let i = 0; i < this.parameters.length; i++ )
    {
      let p = this.parameters[ i ];
      let canBeOmitted = p.optional || p.variadic;

      if ( ! canBeOmitted && i >= values.length )
      {
        return false;
      }

      if ( p.type !== values[ i ].type )
      {
        return false;
      }

    }

    if ( this.parameters.length > 0 && this.parameters[ this.parameters.length - 1 ].variadic )
    {
      let p = this.parameters[ this.parameters.length - 1 ];

      for ( let i = this.parameters.length - 1; i < values.length; i++ )
      {
        if ( p.type !== values[ i ].type )
        {
          return false;
        }
      }
    }

    return true;
  }

  static create( name:string, callback:LomaFunctionCallback, parameters:ParameterDataType<any>[], returnType:DataType )
  {
    let lomaFunction = new LomaFunction();
    
    lomaFunction.functionName = name;
    lomaFunction.callback = callback;

    lomaFunction.parameters = parameters;
    lomaFunction.returnType = returnType;

    return lomaFunction;
  }


  static forNumberIO( name:string, parameterName:string, numeric:( n:number ) => number ):LomaFunction
  {
    let callback:LomaFunctionCallback = ( e, fe, values )=>
    {
      let inputValue = values[ 0 ].data as number;
      let outputValue = numeric( inputValue );
      return EvaluationResult.withValue( outputValue, DataTypes.Number );
    };

    let func = LomaFunction.create( name, callback, [ ParameterDataType.from( parameterName, DataTypes.Number ) ], DataTypes.Number );
    
    return func;
  } 

  
  static forNumbers( name:string, parameterName:string, numeric:( n:number[] ) => number ):LomaFunction
  {
    let callback:LomaFunctionCallback = ( e, fe, values )=>
    {
      let inputValues:number[] = values.map( v => v.data );

      let outputValue = numeric( inputValues );
      return EvaluationResult.withValue( outputValue, DataTypes.Number );
    };

 
    return LomaFunction.create( name, callback, [ ParameterDataType.from( parameterName, DataTypes.Number, true ) ], DataTypes.Number );
  } 

  static forNumbersWith( name:string, parameterNames:string[], numeric:( n:number[] ) => number ):LomaFunction
  {
    let callback:LomaFunctionCallback = ( e, fe, values )=>
    {
      let inputValues:number[] = values.map( v => v.data );

      let outputValue = numeric( inputValues );
      return EvaluationResult.withValue( outputValue, DataTypes.Number );
    };

    let parameters:ParameterDataType<any>[] = [];

    for ( let i = 0; i < parameterNames.length; i++ )
    {
      parameters.push( ParameterDataType.from( parameterNames[ i ], DataTypes.Number, false ) );
    }

 
    return LomaFunction.create( name, callback, parameters, DataTypes.Number );
  }

  static forVector3( name:string, inputName:string, numeric:( n:Vector3[] ) => Vector3 ):LomaFunction
  {
    let callback:LomaFunctionCallback = ( e, fe, values )=>
    {
      let inputValues:Vector3[] = values.map( v => v.data );

      let outputValue = numeric( inputValues );

      return EvaluationResult.withValue( outputValue, DataTypes.Vector3 );
    };

 
    return LomaFunction.create( name, callback, [ ParameterDataType.from( inputName, DataTypes.Vector3, true ) ], DataTypes.Vector3 );
  } 
}